import { CORE_PAGES, useInventoryUploadEligibility } from '@core-ui/components';
import { MoniebookLoader } from '@moniebook-web/components/molecules/moniebook-loader';
import { COOKIE_KEYS, PAGES } from '@moniebook-web/types';
import { getItem, useUserInfo } from '@moniebook-web/utils';
import { Navigate, useLocation } from 'react-router';

export function OnboardingChecks() {
    const {
        emailVerified,
        loading,
        canCreateBusiness,
        currentBusiness,
        businesses,
    } = useUserInfo();

    if (loading) {
        return <MoniebookLoader fullHeight />;
    }

    if (!businesses?.length) {
        return (
            <ChecksWithoutBusiness
                canCreateBusiness={canCreateBusiness}
                emailVerified={emailVerified}
            />
        );
    }

    return (
        <ChecksWithBusiness
            isOwner={currentBusiness?.isOwner}
            numberOfBusinesses={businesses.length}
            emailVerified={emailVerified}
        />
    );
}

export function ChecksWithoutBusiness({
    canCreateBusiness,
    emailVerified,
}: Readonly<{
    canCreateBusiness?: boolean;
    emailVerified?: boolean;
}>) {
    const externalReferralCode = getItem(
        COOKIE_KEYS.EXTERNAL_REFERRAL_CODE_KEY,
    );

    const hasExternalReferralCode = !!externalReferralCode;

    if (!canCreateBusiness) {
        return <Navigate to={PAGES.BUSINESS_ONBOARDING.NO_BUSINESS} replace />;
    }

    if (!emailVerified && !hasExternalReferralCode) {
        return (
            <Navigate
                to={CORE_PAGES.ONBOARDING.SALES_ASSISTED_SOFT_BLOCK}
                replace
            />
        );
    }

    const nextUrl = emailVerified
        ? CORE_PAGES.ONBOARDING.BUSINESS_DETAILS
        : CORE_PAGES.VERIFY_EMAIL;

    return <Navigate to={nextUrl} replace />;
}

const ONBOARDING_ROUTE_PREFIX = '/dashboard/onboarding';
const EXCLUDED_ROUTES = [CORE_PAGES.SETTINGS.SUBSCRIPTIONS];

export function ChecksWithBusiness({
    isOwner,
    numberOfBusinesses,
    emailVerified,
}: Readonly<{
    isOwner?: boolean;
    numberOfBusinesses: number;
    emailVerified?: boolean;
}>) {
    const { pathname } = useLocation();

    const isOnOnboardingRoute = pathname.startsWith(ONBOARDING_ROUTE_PREFIX);
    const isOnExcludedRoute = EXCLUDED_ROUTES.some((route) =>
        pathname.startsWith(route),
    );

    const { shouldNavigateToOnboarding, loading } =
        useInventoryUploadEligibility({
            canGoToOnboardingTasks: false,
            tasks: undefined,
            enabled: !isOnOnboardingRoute && !isOnExcludedRoute,
        });

    if (numberOfBusinesses > 1 && !emailVerified && isOwner) {
        return <Navigate to={CORE_PAGES.VERIFY_EMAIL} replace />;
    }

    if (loading) {
        return <MoniebookLoader fullHeight />;
    }

    if (shouldNavigateToOnboarding) {
        return <Navigate to={CORE_PAGES.ONBOARDING_TASKS} replace />;
    }
    return null;
}
